import Icon from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'types';
import { cn } from 'utils/classNames';

interface BaseProps {
	/** If the button is disabled, or without href for links. */
	disabled?: boolean;

	/** User's first name, required. */
	firstName: string | undefined;

	/** Disable and show a loading spinner instead of the initials. */
	isLoading?: boolean;

	/** A descriptive label for accessibility. */
	label: string;

	/** User's last name, optional. */
	lastName?: string;
}

type AnchorAttrs = AnchorHTMLAttributes<HTMLAnchorElement>;
type ButtonAttrs = ButtonHTMLAttributes<HTMLButtonElement>;

interface AnchorProps
	extends BaseProps, Omit<AnchorAttrs, 'aria-label' | 'children'> {
	type?: never;

	/** Link URL, results in an anchor instead of a button */
	href: string;
}

interface ButtonProps
	extends BaseProps, Omit<ButtonAttrs, 'aria-label' | 'children'> {
	href?: never;

	/** Button click handler */
	onClick: ButtonAttrs['onClick'];
}

type Props = AnchorProps | ButtonProps;

export default function UserProfileButton({
	className,
	disabled,
	firstName,
	href,
	isLoading,
	label,
	lastName,
	type,
	...attrs
}: Props) {
	const isInteractive = !disabled && !isLoading;
	// First name could be an empty string.
	const firstNameInitial = firstName?.[0] ?? '?';
	const lastNameInitial = lastName?.[0] ?? '';
	const hasDoubleInitials = Boolean(lastName);

	return (
		<LinkOrButton
			{...attrs}
			aria-label={label}
			disabled={!isInteractive}
			href={href}
			type={type}
			className={cn(
				'group/user-profile-button inline-flex size-12 items-center justify-center rounded-full align-middle font-bold',
				hasDoubleInitials && 'text-xs',
				// Match borderless IconButton.
				isInteractive && 'hover:bg-inverse/20 active:bg-inverse/30',
				className,
			)}
		>
			<span
				className={cn(
					'relative inline-flex size-9 items-center justify-center rounded-full border-2 border-inverse',
					// Thinner border in forced colors mode.
					'forced-colors:border',
					isInteractive &&
						'forced-colors:group-hover/user-profile-button:outline-solid',
					!disabled && 'bg-inverse text-inverse',
					disabled && 'bg-disabled text-disabled',
				)}
			>
				{isLoading ? (
					<Icon name="loading" />
				) : (
					`${firstNameInitial}${lastNameInitial}`
				)}
			</span>
		</LinkOrButton>
	);
}
UserProfileButton.displayName = 'UserProfileButton';
